import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { getAccessSystemZones } from "app/services/api/accessSystemZones";
import {
  DEFAULT_ZONE_ACCESS,
  eligibleDevicesFromSelectedZones,
  getZoneIdFromApiRow,
  getZoneLabelFromApiRow,
  mergeZoneAccessOnZoneSelection,
  normalizeOverrideRule,
  normalizeZoneEntryRule,
} from "../zoneAccessProductHelpers";

/**
 * Ticket product — zoneAccess (zones, entry rules, device types, device overrides).
 */
const ProductZoneAccessSection = ({ values, setFieldValue, t }) => {
  const [allZones, setAllZones] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    (async () => {
      try {
        const rows = await getAccessSystemZones();
        if (cancelled) return;
        setAllZones(Array.isArray(rows) ? rows : []);
        setLoadError(false);
      } catch (_e) {
        if (!cancelled) {
          setAllZones([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeZones = React.useMemo(
    () =>
      (allZones || []).filter(
        (z) => z && typeof z === "object" && z.isActive !== false,
      ),
    [allZones],
  );

  const optionRows = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const z of activeZones) {
      const id = getZoneIdFromApiRow(z);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(z);
    }
    return out;
  }, [activeZones]);

  const za = values.zoneAccess || { ...DEFAULT_ZONE_ACCESS };

  const zoneById = React.useMemo(
    () => new Map((allZones || []).map((z) => [getZoneIdFromApiRow(z), z])),
    [allZones],
  );

  const selectedOptions = React.useMemo(() => {
    const rows = Array.isArray(za.zones) ? za.zones : [];
    return rows
      .map((r) => {
        const id = String(r.zoneId ?? "").trim();
        if (!id) return null;
        return zoneById.get(id) || { _id: id, name: id, isActive: false };
      })
      .filter(Boolean);
  }, [za.zones, zoneById]);

  const selectedZoneIds = React.useMemo(
    () =>
      (Array.isArray(za.zones) ? za.zones : [])
        .map((r) => String(r.zoneId ?? "").trim())
        .filter(Boolean),
    [za.zones],
  );

  const eligibleDevices = React.useMemo(
    () => eligibleDevicesFromSelectedZones(allZones, selectedZoneIds),
    [allZones, selectedZoneIds],
  );

  const handleZonesChange = (_e, newRows) => {
    const ids = (newRows || [])
      .map((z) => getZoneIdFromApiRow(z))
      .filter(Boolean);
    const next = mergeZoneAccessOnZoneSelection(za, ids, allZones);
    setFieldValue("zoneAccess", next);
  };

  const setEntryRule = (zoneId, entryRule) => {
    const id = String(zoneId).trim();
    setFieldValue("zoneAccess", {
      ...za,
      zones: (Array.isArray(za.zones) ? za.zones : []).map((row) =>
        String(row.zoneId) === id
          ? { ...row, entryRule: normalizeZoneEntryRule(entryRule) }
          : row,
      ),
    });
  };

  const setDeviceOverrideRule = (deviceId, rule) => {
    const id = String(deviceId).trim();
    const nextRule = normalizeOverrideRule(rule) || "single_use";
    const list = Array.isArray(za.deviceOverrides)
      ? [...za.deviceOverrides]
      : [];
    const idx = list.findIndex((o) => String(o.deviceId) === id);
    if (idx >= 0) list[idx] = { deviceId: id, rule: nextRule };
    else list.push({ deviceId: id, rule: nextRule });
    const map = new Map(list.map((o) => [String(o.deviceId).trim(), o]));
    setFieldValue("zoneAccess", {
      ...za,
      deviceOverrides: Array.from(map.values()),
    });
  };

  const getDeviceRuleValue = (deviceId) => {
    const row = (za.deviceOverrides || []).find(
      (o) => String(o.deviceId) === String(deviceId),
    );
    const r = normalizeOverrideRule(row?.rule);
    return r || "single_use";
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {t("pages.dsokProducts.zoneAccess.title", {
          defaultValue: "Access rules",
        })}
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t("pages.dsokProducts.zoneAccess.help.title", {
            defaultValue: "How access rules work",
          })}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t("pages.dsokProducts.zoneAccess.help.intro", {
            defaultValue:
              "Access rules define how a ticket can move through zones and devices.",
          })}
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {t("pages.dsokProducts.zoneAccess.help.zonesHeading", {
            defaultValue: "Zones",
          })}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t("pages.dsokProducts.zoneAccess.help.zonesBody", {
            defaultValue:
              "A ticket may be allowed to enter one or more zones. Each zone defines where the visitor is allowed to go.",
          })}
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {t("pages.dsokProducts.zoneAccess.help.entryRuleHeading", {
            defaultValue: "Entry rule",
          })}
        </Typography>
        <Box
          component="ul"
          sx={{ m: 0, pl: 2.5, mb: 1.5, "& li": { mb: 0.25 } }}
        >
          <Box component="li">
            <Typography variant="body2">
              {t("pages.dsokProducts.zoneAccess.help.entryRuleSingle", {
                defaultValue:
                  "Single entry — the visitor may enter the zone only once.",
              })}
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2">
              {t("pages.dsokProducts.zoneAccess.help.entryRuleMulti", {
                defaultValue:
                  "Multiple entries — the visitor may enter the zone multiple times.",
              })}
            </Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {t("pages.dsokProducts.zoneAccess.help.deviceTypesHeading", {
            defaultValue: "Device types",
          })}
        </Typography>
        <Box
          component="ul"
          sx={{ m: 0, pl: 2.5, mb: 1.5, "& li": { mb: 0.25 } }}
        >
          <Box component="li">
            <Typography variant="body2">
              {t("pages.dsokProducts.zoneAccess.help.deviceTypesTurnstile", {
                defaultValue:
                  "Turnstile — standard turnstile access.",
              })}
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2">
              {t("pages.dsokProducts.zoneAccess.help.deviceTypesGate", {
                defaultValue:
                  "Gate — wider access typically used for strollers or wheelchairs.",
              })}
            </Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          {t("pages.dsokProducts.zoneAccess.help.overridesHeading", {
            defaultValue: "Device overrides",
          })}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("pages.dsokProducts.zoneAccess.help.overridesIntro", {
            defaultValue:
              "Specific devices inside a zone may have different rules than the default zone rule. For example, a zone may allow only one entry, but a specific device inside the zone may allow multiple uses.",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("pages.dsokProducts.zoneAccess.help.overridesPriority", {
            defaultValue: "Overrides always take priority over the zone rule.",
          })}
        </Typography>
      </Alert>

      {loadError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("pages.dsokProducts.zoneAccess.loadError", {
            defaultValue:
              "Could not load zones. You can still edit other product fields.",
          })}
        </Alert>
      ) : null}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1,
            mb: 1,
          }}
        >
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {t("pages.dsokProducts.zoneAccess.loading", {
              defaultValue: "Loading zones…",
            })}
          </Typography>
        </Box>
      ) : null}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {t("pages.dsokProducts.zoneAccess.zonesBlockTitle", {
          defaultValue: "Zones",
        })}
      </Typography>

      {!loading && !loadError && optionRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("pages.dsokProducts.zoneAccess.emptyZones", {
            defaultValue:
              "No active zones are defined. Create zones under Access system → Zones.",
          })}
        </Typography>
      ) : null}

      <Autocomplete
        multiple
        disabled={loading}
        options={optionRows}
        value={selectedOptions}
        onChange={handleZonesChange}
        getOptionLabel={(z) => getZoneLabelFromApiRow(z)}
        isOptionEqualToValue={(a, b) =>
          getZoneIdFromApiRow(a) === getZoneIdFromApiRow(b)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("pages.dsokProducts.zoneAccess.selectZones", {
              defaultValue: "Select zones",
            })}
            placeholder={t(
              "pages.dsokProducts.zoneAccess.allowedZonesPlaceholder",
              {
                defaultValue: "Select zones",
              },
            )}
          />
        )}
        sx={{ mb: 2 }}
      />

      {selectedZoneIds.length > 0 ? (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {(Array.isArray(za.zones) ? za.zones : []).map((row) => {
            const zid = String(row.zoneId ?? "").trim();
            if (!zid) return null;
            const meta = zoneById.get(zid);
            const label = meta ? getZoneLabelFromApiRow(meta) : zid;
            const er = normalizeZoneEntryRule(row.entryRule);
            return (
              <Box
                key={zid}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ minWidth: 140, flex: "1 1 120px" }}
                >
                  {label}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id={`zone-entry-${zid}`}>
                    {t("pages.dsokProducts.zoneAccess.entryRule", {
                      defaultValue: "Entry rule",
                    })}
                  </InputLabel>
                  <Select
                    labelId={`zone-entry-${zid}`}
                    label={t("pages.dsokProducts.zoneAccess.entryRule", {
                      defaultValue: "Entry rule",
                    })}
                    value={er}
                    onChange={(e) => setEntryRule(zid, e.target.value)}
                  >
                    <MenuItem value="single_entry">
                      {t("pages.dsokProducts.zoneAccess.entryRuleSingleEntry", {
                        defaultValue: "Single entry",
                      })}
                    </MenuItem>
                    <MenuItem value="multi_entry">
                      {t("pages.dsokProducts.zoneAccess.entryRuleMultiEntry", {
                        defaultValue: "Multi entry",
                      })}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            );
          })}
        </Stack>
      ) : null}

      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 0.5, mt: 1 }}
      >
        {t("pages.dsokProducts.zoneAccess.deviceTypesTitle", {
          defaultValue: "Device types",
        })}
      </Typography>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={za.allowTurnstile !== false}
              onChange={(e) =>
                setFieldValue("zoneAccess", {
                  ...za,
                  allowTurnstile: e.target.checked,
                })
              }
            />
          }
          label={t("pages.dsokProducts.zoneAccess.allowTurnstile", {
            defaultValue: "Allow turnstile",
          })}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={za.allowGate !== false}
              onChange={(e) =>
                setFieldValue("zoneAccess", {
                  ...za,
                  allowGate: e.target.checked,
                })
              }
            />
          }
          label={t("pages.dsokProducts.zoneAccess.allowGate", {
            defaultValue: "Allow gate",
          })}
        />
      </Stack>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {t("pages.dsokProducts.zoneAccess.deviceOverridesTitle", {
          defaultValue: "Device individual settings",
        })}
      </Typography>

      {selectedZoneIds.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("pages.dsokProducts.zoneAccess.deviceOverridesNeedZones", {
            defaultValue:
              "Select at least one zone to configure device overrides.",
          })}
        </Typography>
      ) : eligibleDevices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("pages.dsokProducts.zoneAccess.noDevicesInZones", {
            defaultValue:
              "No devices are assigned to the selected zones in the access system.",
          })}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("pages.dsokProducts.zoneAccess.colZone", {
                  defaultValue: "Zone",
                })}
              </TableCell>
              <TableCell>
                {t("pages.dsokProducts.zoneAccess.colDevice", {
                  defaultValue: "Device",
                })}
              </TableCell>
              <TableCell sx={{ minWidth: 160 }}>
                {t("pages.dsokProducts.zoneAccess.colOverrideRule", {
                  defaultValue: "Override rule",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eligibleDevices.map((d) => (
              <TableRow key={d.deviceId}>
                <TableCell>{d.zoneName}</TableCell>
                <TableCell>
                  {d.deviceName && String(d.deviceName).trim() !== ""
                    ? d.deviceName
                    : t("pages.dsokProducts.zoneAccess.deviceNameFallback", {
                        defaultValue: "Unnamed device",
                      })}
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={getDeviceRuleValue(d.deviceId)}
                      onChange={(e) =>
                        setDeviceOverrideRule(d.deviceId, e.target.value)
                      }
                    >
                      <MenuItem value="deny">
                        {t("pages.dsokProducts.zoneAccess.ruleDeny", {
                          defaultValue: "Deny",
                        })}
                      </MenuItem>
                      <MenuItem value="single_use">
                        {t("pages.dsokProducts.zoneAccess.ruleSingleUse", {
                          defaultValue: "Single use",
                        })}
                      </MenuItem>
                      <MenuItem value="multi_use">
                        {t("pages.dsokProducts.zoneAccess.ruleMultiUse", {
                          defaultValue: "Multi use",
                        })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default ProductZoneAccessSection;
